// Instrumentos en 3D: analizador lógico USB de 8 canales.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { LA } from '../../parts/tools.js';
import { PITCH } from '../../parts/registry.js';

let labelTex = null;

function topLabel() {
  if (labelTex) return labelTex;
  const { c, g } = canvas(LA.w, LA.d, 16);
  g.fillStyle = '#1b1d21';
  g.fillRect(0, 0, LA.w, LA.d);
  g.strokeStyle = '#3d424b';
  g.lineWidth = 0.3;
  g.strokeRect(1.5, 1.5, LA.w - 3, LA.d - 3);
  text(g, 'LOGIC', LA.w / 2, 12, { size: 4.2, color: '#e9edf2', weight: 800 });
  text(g, '8 CH · 24 MHz', LA.w / 2, 17.5, { size: 2.1, color: '#9aa3ad', weight: 600 });
  // Forma de onda decorativa
  g.strokeStyle = '#22b8c8';
  g.lineWidth = 0.55;
  g.beginPath();
  let x = 4;
  let hi = false;
  g.moveTo(x, 27);
  for (const w of [2, 3, 1.5, 4, 2, 2.5, 3]) {
    x += w;
    g.lineTo(x, hi ? 23 : 27);
    hi = !hi;
    g.lineTo(x, hi ? 23 : 27);
  }
  g.stroke();
  for (let i = 0; i < 8; i++) text(g, String(i), LA.w / 2 + (i - 4) * PITCH, LA.d - 4.2, { size: 1.6, color: '#e9edf2', weight: 700 });
  text(g, '⏚', LA.w / 2 + 4 * PITCH, LA.d - 4.2, { size: 2, color: '#e9edf2', weight: 700 });
  labelTex = texture(c);
  labelTex.userData.shared = true;
  return labelTex;
}

export function buildLogicAnalyzer() {
  const b = new Builder();
  const body = mat('#23262b', { roughness: 0.35, metalness: 0.6 });
  b.rbox(LA.w, LA.h, LA.d, 2.2, body, 0, 0, 0);
  // Cable USB hacia atrás
  const cable = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 4, -LA.d / 2),
    new THREE.Vector3(0, 3.5, -LA.d / 2 - 12),
    new THREE.Vector3(8, 2, -LA.d / 2 - 30),
    new THREE.Vector3(26, 1.5, -LA.d / 2 - 42),
  ]);
  b.add(new THREE.TubeGeometry(cable, 30, 1.6, 10, false), mat('#121315', { roughness: 0.6 }));
  b.box(9, 5.5, 7, M.blackPlastic, 0, 1.3, -LA.d / 2 - 3);
  // Conector macho de 9 pines al frente
  const zH = LA.d / 2 + 1.6;
  b.box(9 * PITCH, 2.5, PITCH, M.blackPlastic, 0, LA.h - 1, zH);
  for (let i = 0; i < 9; i++) b.box(0.64, 7.6, 0.64, M.gold, (i - 4) * PITCH, LA.h - 1.4, zH);
  b.box(9 * PITCH + 1, LA.h - 1, 4, body, 0, 0, LA.d / 2 + 1);
  b.build();
  const top = new THREE.Mesh(new THREE.PlaneGeometry(LA.w - 1, LA.d - 1).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: topLabel(), roughness: 0.4, metalness: 0.3 }));
  top.position.y = LA.h + 0.01;
  b.root.add(top);
  const ledMat = emissiveMat('#3a1414', '#ff3b2f');
  const led = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.6, 14), ledMat);
  led.position.set(LA.w / 2 - 4.5, LA.h + 0.2, -LA.d / 2 + 5);
  b.root.add(led);
  const halo = glow('#ff3b2f', 7);
  halo.position.set(LA.w / 2 - 4.5, LA.h + 1.2, -LA.d / 2 + 5);
  b.root.add(halo);
  return {
    root: b.root,
    update() {
      setEmissive(ledMat, 0.8, 12);
      setGlow(halo, 0.6);
    },
    reset() {
      setEmissive(ledMat, 0, 12);
      setGlow(halo, 0);
    },
  };
}

export const MODELS = { 'logic-analyzer': buildLogicAnalyzer };
